import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS, LabelS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_SERIES_SQUAD } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { css } from '@sorare/css';

import { useEnablePublicSquads } from '#src/hooks/squad/useEnablePublicSquads.ts';

import { SquadInviteButton } from '../SquadInviteButton/index.tsx';
import { JoinSecretShare } from './JoinSecretShare';

const Root = styled(Vertical)`
  position: relative;
  z-index: 1;
  width: 100%;
  padding: var(--double-unit);
`;

const Card = styled(Vertical)`
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  width: 100%;
`;

const inviteRoot = css`
  position: relative;
  z-index: 1;
  width: 100%;
  padding: var(--double-unit) var(--quadruple-unit);
`;

type Props = {
  squadId: string;
  joinSecret: string;
  membersCount: number;
  maxMembersCount: number;
};

export const SquadInviteBlock = ({
  squadId,
  joinSecret,
  membersCount,
  maxMembersCount,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const enablePublicSquads = useEnablePublicSquads();

  const continueButton = (
    <Button
      size="large"
      color="primary"
      to={generateSportContextPath(ANY_SPORT_SERIES_SQUAD)}
      fullWidth
    >
      <FormattedMessage defaultMessage="Continue" />
    </Button>
  );

  if (enablePublicSquads) {
    return (
      <Vertical gap={1} className={inviteRoot}>
        <SquadInviteButton squadId={squadId} />
        {continueButton}
      </Vertical>
    );
  }

  return (
    <Root gap={2}>
      <SBHorizontal>
        <HeadlineS>
          <FormattedMessage defaultMessage="Invite your friends" />
        </HeadlineS>
        <LabelS color="var(--c-nd-600)">
          <FormattedMessage
            defaultMessage="({membersCount}/{max})"
            values={{ membersCount, max: maxMembersCount }}
          />
        </LabelS>
      </SBHorizontal>
      <Card gap={2}>
        <JoinSecretShare joinSecret={joinSecret} />
        {continueButton}
      </Card>
    </Root>
  );
};
